"use client";

import { useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { ThemeProvider } from "@/providers/theme-provider";
import { QueryProvider } from "@/providers/query-provider";
import { ToastProvider } from "@/providers/toast-provider";
import { ServiceWorkerCleanup } from "@/components/service-worker-cleanup";
import { PLUGINS } from "@/config/constants";
import { captureAttributionFromUrl } from "@/lib/integrations/utm";
import { router } from "@/app/router";

function ThirdPartyScripts() {
  useEffect(() => {
    if (PLUGINS.googleAnalytics.isActive) {
      injectGtm(PLUGINS.googleAnalytics.gtmId);
    }

    captureAttributionFromUrl();

    if (PLUGINS.rewardful.isActive) {
      injectRewardful();
    }

    if (PLUGINS.intercom.isActive) {
      injectIntercom(PLUGINS.intercom.appId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function injectGtm(gtmId: string) {
  if (document.getElementById("gtm-init")) return;
  const script = document.createElement("script");
  script.id = "gtm-init";
  script.async = true;
  appendScript(script, buildGtmSnippet(gtmId));
  document.head.appendChild(script);
}

function buildGtmSnippet(gtmId: string): string {
  return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
}

function injectRewardful() {
  if (document.getElementById("rewardful-init")) return;
  const initScript = document.createElement("script");
  initScript.id = "rewardful-init";
  initScript.async = true;
  appendScript(initScript, `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`);
  document.head.appendChild(initScript);

  const rwScript = document.createElement("script");
  rwScript.async = true;
  rwScript.src = "https://r.wdfl.co/rw.js";
  rwScript.dataset.rewardful = "de11e2";
  document.head.appendChild(rwScript);
}

function injectIntercom(appId: string) {
  if (document.getElementById("intercom-boot")) return;
  (window as unknown as Record<string, unknown>).intercomSettings = {
    api_base: "https://api-iam.intercom.io",
    app_id: appId,
    hide_default_launcher: true,
  };
  const script = document.createElement("script");
  script.id = "intercom-boot";
  script.async = true;
  appendScript(script, buildIntercomSnippet(appId));
  document.body.appendChild(script);
}

function buildIntercomSnippet(appId: string): string {
  return `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${appId}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`;
}

function appendScript(scriptEl: HTMLScriptElement, code: string): void {
  scriptEl.appendChild(document.createTextNode(code));
}

export function App() {
  return (
    <>
      <ServiceWorkerCleanup />
      <ThirdPartyScripts />
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <QueryProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </QueryProvider>
      </ThemeProvider>
    </>
  );
}
